// Generator dokumen "Tender Invitation" untuk tab Sending Invitation to DMUT.
//
// Meniru contoh PDF acuan (Tender_Invitation_PP-CRD-2026-0004...): kop PT KPI,
// paragraf undangan, tabel Buyer + Cargo Requirement (nested), satu baris per
// clause Planning (judul + "(Mandatory)" + isi rich-text), lalu paragraf batas
// pemasukan penawaran (submission deadline & validity WIB) + catatan GCG.
//
// Dibangun murni client-side sebagai HTML (tanpa library PDF):
//  - previewInvitation  -> buka dokumen di tab baru.
//  - printInvitationPdf -> buka lalu panggil window.print() sehingga user bisa
//    Save as PDF dari dialog print browser. Kontrak BE belum punya endpoint
//    generate dokumen undangan; kalau nanti ada, kedua tombol tinggal dialihkan.

import { formatDateTimeWib } from '../../formatters';
import { isoDateJakarta } from './TenderInfoCards';

const esc = s =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Satu baris nested-table Cargo Requirement per cargo.
const cargoRowsHtml = cargos =>
  (cargos || [])
    .map(c => {
      const crudeList = (c.crudes || [])
        .map(
          cr =>
            `<li>${esc(cr.crudeName)}${
              cr.maxCapacity != null ? ` (Max ${esc(cr.maxCapacity)} MB)` : ''
            }</li>`
        )
        .join('');
      const parcel =
        c.volume != null && c.volume !== ''
          ? `${esc(c.volume)} (±) ${c.ot != null ? esc(c.ot) : '-'}%`
          : '-';
      return `<tr>
        <td><ul class="crude-list">${crudeList}</ul></td>
        <td class="center bold">${esc((c.incoterms || []).join('/') || '-')}<br/><br/>${esc(c.plant || '')}<br/>${esc(c.unit || '')}</td>
        <td class="center bold">${parcel}</td>
        <td class="center bold">${isoDateJakarta(c.ddrFrom)} s/d ${isoDateJakarta(c.ddrTo)}</td>
      </tr>`;
    })
    .join('');

// Baris tabel utama per clause Planning. `summernote` memang HTML dari editor
// rich-text internal — disisipkan apa adanya.
const clauseRowsHtml = clauses =>
  (clauses || [])
    .map(
      c => `<tr>
        <td class="label">${esc(c.title)}${
          c.mandatory ? '<br/><br/>(Mandatory)' : ''
        }</td>
        <td>${c.summernote || '-'}</td>
      </tr>`
    )
    .join('');

export const buildInvitationHtml = ({ record, detail }) => {
  const cargos = detail?.requirement?.cargos || [];
  const clauses = detail?.requirement?.clauses || [];
  const validity = detail?.requirement?.validity || {};
  const refLabel = `Tender Invitation Ref: ${esc(record?.procId || '-')}`;
  const contractType = detail?.requirement?.contractType || '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Tender_Invitation_${esc(record?.procId || '')}_${esc(
    (record?.title || '').replace(/\s+/g, '_')
  )}</title>
<style>
  /* Preview di layar meniru kertas A4: lembar putih 210mm di tengah latar
     abu-abu. Saat print, lembarnya dilepas jadi konten halaman biasa. */
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #222; margin: 0; background: #9a9a9a; }
  .sheet { width: 210mm; min-height: 297mm; margin: 24px auto; padding: 15mm 18mm; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.45); box-sizing: border-box; }
  .kop h1 { color: #1f4e9d; font-size: 16px; margin: 0; }
  .kop .division { color: #8a8a8a; letter-spacing: .5px; font-size: 10px; text-transform: uppercase; border-bottom: 2px solid #e0e0e0; padding-bottom: 6px; margin-top: 2px; }
  p.intro { margin: 14px 0 10px; }
  /* table-layout fixed + break-word supaya isi clause yang lebar (termasuk
     tabel di dalam rich-text) tidak mendorong tabel melebihi lebar kertas —
     kalau melebihi, garis tepi kanan terpotong keluar halaman. */
  /* Border memakai satuan mm, bukan px — border 1px sering hilang sebagian di
     hasil print/PDF karena pembulatan sub-pixel saat halaman diskalakan. */
  table.main { border-collapse: collapse; width: 100%; table-layout: fixed; border: 0.35mm solid #555; }
  table.main > tbody > tr > td { border: 0.35mm solid #555; padding: 6px 8px; vertical-align: top; word-wrap: break-word; overflow-wrap: anywhere; }
  td.label { width: 130px; font-weight: bold; }
  /* Konten rich-text clause: apapun di dalamnya dipaksa muat di kolom. */
  table.main td table { max-width: 100% !important; width: 100% !important; table-layout: fixed; border-collapse: collapse; }
  table.main td img { max-width: 100%; height: auto; }
  table.cargo { border-collapse: collapse; width: 100%; }
  table.cargo th, table.cargo td { border: 0.35mm solid #555; padding: 5px 7px; vertical-align: top; }
  table.cargo th { text-align: center; font-size: 10px; }
  ul.crude-list { margin: 0; padding-left: 16px; }
  .center { text-align: center; vertical-align: middle !important; }
  .bold { font-weight: bold; }
  .note { font-size: 10px; margin-top: 6px; padding: 0 8px 6px; }
  .closing { margin-top: 16px; }
  ul.gcg { margin: 4px 0 0; padding-left: 16px; font-size: 10px; }
  .regards { margin-top: 18px; }
  .footer { text-align: center; color: #999; font-size: 9px; margin-top: 28px; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { margin: 0; background: #fff; }
    .sheet { width: auto; min-height: 0; margin: 0; padding: 0; background: #fff; box-shadow: none; }
  }
</style>
</head>
<body>
<div class="sheet">
  <div class="kop">
    <h1>PT KILANG PERTAMINA INTERNASIONAL</h1>
    <div class="division">Crude Oil Supply &amp; Trading Procurement Division</div>
  </div>

  <p class="intro">
    We are pleased to invite your esteemed company to submit your best offer to
    our ${esc(contractType || 'Spot')} buying requirement for
    <b>${esc(record?.title || '-')}</b> with the attached Terms and Conditions:
  </p>

  <table class="main">
    <tbody>
      <tr>
        <td class="label">Buyer</td>
        <td><b>PT KILANG PERTAMINA INTERNASIONAL (&ldquo;PT KPI&rdquo;)</b></td>
      </tr>
      <tr>
        <td class="label">Cargo Requirement</td>
        <td style="padding:0;">
          <table class="cargo">
            <thead>
              <tr>
                <th>CRUDE OIL</th>
                <th>DELIVERY</th>
                <th>PARCEL SIZE (KB)*</th>
                <th>DELIVERY DATE RANGE<br/>AT DISCHARGE PORT*</th>
              </tr>
            </thead>
            <tbody>${cargoRowsHtml(cargos)}</tbody>
          </table>
          <div class="note">
            *Note:<br/>
            Blended/mixed crude should be blended on the vessel with one B/L of
            blended/mixed crude. Blending composition should be stated.
            <ul class="gcg">
              <li>Seller to offer 3-day-DDR for DAP basis within the above date range for each cargo. Buyer will narrow down to 2-days-ADD for DAP offer.</li>
              <li>Seller may offer with other parcel size and DDR other than stated in PT KPI requirement above. Acceptance will be subject to further evaluation by PT KPI.</li>
            </ul>
          </div>
        </td>
      </tr>
      ${clauseRowsHtml(clauses)}
    </tbody>
  </table>

  <div class="closing">
    <p>
      Please submit your best offer only to
      <b>crudeprocurement@pertamina.com</b> not later than:
      <b>${formatDateTimeWib(validity.submissionDeadline)}</b>, with validity
      until <b>${formatDateTimeWib(validity.validityPeriod)}</b>.
    </p>
    <b>Please note the following:</b>
    <ul class="gcg">
      <li>PT KPI will further negotiate to the shortlisted bidder(s) based on further evaluation.</li>
      <li>PT KPI reserve the right to withdraw this tender at any time without award or to decline to award after receiving offers at its sole discretion.</li>
      <li>PT KPI will evaluate counterparty participation in the tender process and based on its internal evaluation by sole discretion and has the right not to invite any counterparty in future tender.</li>
      <li>PT KPI respects Good Corporate Governance (GCG). Report any violation to Pertamina whistle blowing system: pertaminaclean@tipoffs.com.sg</li>
    </ul>
    <p>Thank you for your attention and cooperation.</p>
    <div class="regards">
      <b>Best Regards,</b><br/>
      <b>Crude Oil Supply</b><br/>
      <b style="color:#1f4e9d;">PT KILANG PERTAMINA INTERNASIONAL</b>
    </div>
  </div>

  <div class="footer">${refLabel}</div>
</div>
</body>
</html>`;
};

const openDocWindow = data => {
  const w = window.open('', '_blank');
  if (!w) return null; // popup diblokir browser
  w.document.write(buildInvitationHtml(data));
  w.document.close();
  return w;
};

export const previewInvitation = data => openDocWindow(data);

export const printInvitationPdf = data => {
  const w = openDocWindow(data);
  if (!w) return;
  // Tanpa aset eksternal, dokumen siap segera — beri jeda kecil supaya layout
  // selesai sebelum dialog print (user memilih "Save as PDF" di sana).
  w.focus();
  setTimeout(() => w.print(), 400);
};
